/**
 * Avanzamento notifica CRA Art.14(2) — vista "livello di progresso" del thread SRP.
 *
 * Combina due pattern (design A+D):
 *  - D  Banner "prossima azione": la singola azione più urgente sul thread,
 *       colorata per urgenza (ok/warning/breached), con countdown live e CTA.
 *  - A  Stepper orizzontale a 3 nodi (early warning 24h · notifica 72h ·
 *       relazione finale 14gg, o 30gg dalla notifica 72h per i Severe Incident): ogni nodo
 *       codifica lo stato di consegna (da fare · attivo · step registrato · scaduto), con la
 *       finestra SLA e il countdown live sullo step attivo.
 *
 * Timer: registrare lo step (CTA «Registra» → conferma del pop-up) FERMA il clock dello step
 * all'istante della registrazione; il nodo mostra se è stato registrato in tempo o in ritardo e
 * non scade più. Un thread chiuso (risolto / non necessario) ferma i timer degli step mancanti.
 *
 * È puramente presentazionale: riceve `report` e calcola gli stati riusando le stesse
 * costanti/logica di SrpListPage e SrpThreadDetail. L'azione vera (registrazione dello step)
 * resta nella lista operativa del dettaglio: `onActionClick` porta l'analista allo step
 * attivo.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { formatRemainingMs } from './SlaCountdown.js';
import type { NotificationStep, SlaStatus, SlaTimer, SrpReport } from './types.js';
import { slaAnchorMs } from './slaAnchor.js';
import { SLA_STEP_ORDER, finalRuleText, stepDeadlineMs, stepWindowLabel } from './slaDeadlines.js';
import { isThreadChiuso } from './threadStatus.js';
import styles from './Srp.module.css';

// ---------------------------------------------------------------------------
// Costanti SLA (scadenze in slaDeadlines.ts, coerenti con srp-service)
// ---------------------------------------------------------------------------

/** Finestra "amber" fissa prima di ogni scadenza (WARNING_THRESHOLD_MS lato service). */
const WARNING_MS = 6 * 60 * 60 * 1000;

const STEP_NAME: Record<NotificationStep, string> = {
  early_warning: 'Early warning',
  vulnerability: 'Notifica 72h',
  final: 'Relazione finale',
};

/** Dettaglio dell'azione per il banner, per step (relazione finale: regola della categoria). */
function stepActionHint(report: SrpReport, step: NotificationStep): string {
  if (step === 'early_warning') return 'notifica di preallarme entro 24h dal rilevamento';
  if (step === 'vulnerability') return 'notifica formale ENISA entro 72h dal rilevamento';
  return `relazione finale ${finalRuleText(report)}`;
}

/** Etichetta dell'azione «registra lo step» (CTA del banner, pop-up del dettaglio). */
export const REGISTRA_STEP_LABEL = 'Registra';

/** Tooltip dell'azione «registra lo step»: chiarisce che a ENISA non parte nulla. */
export const REGISTRA_STEP_TOOLTIP =
  'questo processo al momento è solo interno e NON invia a ENISA alcuna notifica. Qui stiamo tracciando internamente - notificando le persone interessate - che questo passo di notifica è stato effettuato manualmente sul sito ENISA. La registrazione ferma il timer SLA dello step';

// ---------------------------------------------------------------------------
// Tipi e helper di calcolo
// ---------------------------------------------------------------------------

/** Stato di uno step (identico a SrpThreadDetail.statoStep): registrato come fatto o no. */
type DeliveryState = 'todo' | 'delivered';

/** Stato visivo del nodo nello stepper. */
type NodeState = 'done' | 'active' | 'warn' | 'crit' | 'future';

/** Livello di urgenza del banner. */
type Urgency = 'ok' | 'warn' | 'crit' | 'done';

interface StepView {
  step: NotificationStep;
  delivery: DeliveryState;
  node: NodeState;
  /** Scadenza assoluta (ms) dello step, oppure null se manca l'ancora SLA. */
  deadlineMs: number | null;
  /** Istante di consegna confermata (ms), se disponibile: lì il timer dello step si è fermato. */
  deliveredAtMs: number | null;
  /** Nota salvata con lo step registrato (se presente). */
  note?: string;
}

/** Hook che restituisce `Date.now()` aggiornato a intervalli regolari (countdown live). */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => {
      setNow(Date.now());
    }, intervalMs);
    return () => {
      clearInterval(id);
    };
  }, [intervalMs]);
  return now;
}

/** Stato SLA di una scadenza rispetto a `now`. */
function slaStatus(remainingMs: number): SlaStatus {
  if (remainingMs <= 0) return 'breached';
  if (remainingMs < WARNING_MS) return 'warning';
  return 'ok';
}

function classeNodo(node: NodeState): string {
  switch (node) {
    case 'done':
      return styles.nodeDone ?? '';
    case 'active':
      return styles.nodeActive ?? '';
    case 'warn':
      return styles.nodeWarn ?? '';
    case 'crit':
      return styles.nodeCrit ?? '';
    default:
      return styles.nodeFuture ?? '';
  }
}

function classeUrgenza(u: Urgency): string {
  if (u === 'warn') return styles.uWarn ?? '';
  if (u === 'crit') return styles.uCrit ?? '';
  if (u === 'done') return styles.uDone ?? '';
  return styles.uOk ?? '';
}

function fmtData(ms: number): string {
  return new Date(ms).toLocaleString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export interface SrpProgressProps {
  report: SrpReport;
  /** Timer SLA del report: ancora del clock CRA (fallback su detectedAt/createdAt). */
  timer?: SlaTimer;
  /**
   * Invocato al click della CTA del banner sullo step attivo (todo):
   * il dettaglio lo usa per portare l'analista alla riga operativa dello step.
   */
  onActionClick?: (step: NotificationStep) => void;
}

export function SrpProgress({ report, timer, onActionClick }: SrpProgressProps) {
  const now = useNow(1000);
  const ancora = slaAnchorMs(report, timer);
  const notifiche = report.payload.notifications ?? [];
  // Thread chiuso: i timer degli step non registrati sono fermi (niente warning/scadenze).
  const timerFermi = isThreadChiuso(report);

  function deliveryState(step: NotificationStep): {
    delivery: DeliveryState;
    deliveredAtMs: number | null;
    note?: string;
  } {
    // Uno step è completato quando è stato registrato come fatto (notifica registrata).
    const notifica = notifiche.find((n) => n.step === step);
    if (!notifica) return { delivery: 'todo', deliveredAtMs: null };
    const ms = new Date(notifica.sentAt).getTime();
    return {
      delivery: 'delivered',
      deliveredAtMs: Number.isNaN(ms) ? null : ms,
      note: notifica.note?.trim() || undefined,
    };
  }

  // Step attivo = primo non ancora completato.
  const deliveries = SLA_STEP_ORDER.map((step) => ({ step, ...deliveryState(step) }));
  const attivoIdx = deliveries.findIndex((d) => d.delivery !== 'delivered');

  const steps: StepView[] = deliveries.map((d, idx) => {
    const deadlineMs = ancora !== null ? stepDeadlineMs(report, ancora, d.step) : null;
    let node: NodeState;
    if (d.delivery === 'delivered') {
      node = 'done';
    } else if (idx !== attivoIdx) {
      node = 'future';
    } else if (deadlineMs !== null && !timerFermi) {
      const s = slaStatus(deadlineMs - now);
      node = s === 'breached' ? 'crit' : s === 'warning' ? 'warn' : 'active';
    } else {
      node = 'active';
    }
    return {
      step: d.step,
      delivery: d.delivery,
      node,
      deadlineMs,
      deliveredAtMs: d.deliveredAtMs,
      note: d.note,
    };
  });

  // ------- Banner "prossima azione" (D) -------
  const attivo = attivoIdx === -1 ? null : steps[attivoIdx];
  let urgency: Urgency = 'ok';
  let bannerTitle = '';
  let bannerDetail = '';
  let ctaLabel: string | null = null;

  if (!attivo) {
    urgency = 'done';
    bannerTitle = 'Tutti gli step registrati';
    bannerDetail = 'Tutti gli step di notifica sono stati registrati — thread completato.';
  } else if (timerFermi) {
    // Thread chiuso: lo step si può ancora registrare, ma il suo timer non scorre.
    urgency = 'done';
    bannerTitle = `Thread chiuso: timer fermi`;
    bannerDetail = `${STEP_NAME[attivo.step]} non registrato — il timer è fermo finché il thread resta chiuso; riaprilo per farlo ripartire.`;
    ctaLabel = REGISTRA_STEP_LABEL;
  } else {
    const nome = STEP_NAME[attivo.step];
    const remaining = attivo.deadlineMs !== null ? attivo.deadlineMs - now : null;
    const st = remaining !== null ? slaStatus(remaining) : 'ok';
    urgency = st === 'breached' ? 'crit' : st === 'warning' ? 'warn' : 'ok';
    bannerTitle = st === 'breached' ? `In ritardo: registra ${nome}` : `Registra ${nome}`;
    ctaLabel = REGISTRA_STEP_LABEL;
    const hint = stepActionHint(report, attivo.step);
    if (remaining === null) {
      bannerDetail = hint;
    } else if (remaining >= 0) {
      bannerDetail = `Scade tra ${formatRemainingMs(remaining)} · ${hint}`;
    } else {
      bannerDetail = `In ritardo di ${formatRemainingMs(-remaining)} · ${hint}`;
    }
  }

  return (
    <div className={styles.progressCard} data-testid="srp-progress">
      {/* D — Banner prossima azione */}
      <div
        className={`${styles.srpNextAction} ${classeUrgenza(urgency)}`}
        data-testid="srp-next-action"
        data-urgency={urgency}
      >
        <span className={styles.srpNextActionIco} aria-hidden="true">
          {urgency === 'done' ? '✓' : urgency === 'crit' ? '⏰' : urgency === 'warn' ? '⚠' : '➜'}
        </span>
        <div className={styles.srpNextActionBody}>
          <p className={styles.srpNextActionKicker}>
            {urgency === 'done' ? 'Completato' : 'Prossima azione'}
          </p>
          <p className={styles.srpNextActionTitle}>{bannerTitle}</p>
          <p className={styles.srpNextActionDetail}>{bannerDetail}</p>
        </div>
        {ctaLabel && attivo && onActionClick && (
          <button
            type="button"
            className={styles.srpNextActionCta}
            data-testid="srp-next-action-cta"
            title={REGISTRA_STEP_TOOLTIP}
            onClick={() => {
              onActionClick(attivo.step);
            }}
          >
            {ctaLabel}
          </button>
        )}
      </div>

      {/* A — Stepper orizzontale */}
      <div className={styles.stepper} data-testid="srp-stepper">
        {steps.map((sv, idx) => {
          const prevDone = idx > 0 && steps[idx - 1]?.node === 'done';
          const segLeft = prevDone ? (styles.segLeftDone ?? '') : '';
          const segRight = sv.node === 'done' ? (styles.segRightDone ?? '') : '';
          const glyph = sv.node === 'done' ? '✓' : String(idx + 1);

          let meta: ReactNode;
          const remaining = sv.deadlineMs !== null ? sv.deadlineMs - now : null;
          if (sv.delivery === 'delivered') {
            // Timer fermato alla registrazione: in tempo o in ritardo rispetto alla scadenza.
            const ritardoMs =
              sv.deliveredAtMs !== null && sv.deadlineMs !== null
                ? sv.deliveredAtMs - sv.deadlineMs
                : null;
            meta = (
              <>
                Registrato
                {sv.deliveredAtMs !== null && (
                  <span className={styles.stepSub}>{fmtData(sv.deliveredAtMs)}</span>
                )}
                {ritardoMs !== null && (
                  <span
                    className={styles.stepSub}
                    data-testid={`timer-fermo-${sv.step}`}
                    data-late={ritardoMs >= 0 ? 'true' : 'false'}
                  >
                    {ritardoMs >= 0
                      ? `timer fermato · in ritardo di ${formatRemainingMs(ritardoMs || 1)}`
                      : 'timer fermato · in tempo'}
                  </span>
                )}
                {sv.note && (
                  <span
                    className={styles.stepNota}
                    title={sv.note}
                    data-testid={`nota-step-${sv.step}`}
                  >
                    «{sv.note}»
                  </span>
                )}
              </>
            );
          } else if (idx !== attivoIdx) {
            meta = <span className={styles.stepSub}>in attesa dello step precedente</span>;
          } else if (timerFermi) {
            meta = (
              <span className={styles.stepSub} data-testid={`timer-fermo-${sv.step}`}>
                timer fermo (thread chiuso)
              </span>
            );
          } else if (remaining !== null) {
            const scaduto = remaining < 0;
            meta = (
              <>
                <span className={styles.stepCountdown}>
                  {scaduto
                    ? `${formatRemainingMs(-remaining)} in ritardo`
                    : formatRemainingMs(remaining)}
                </span>
                {sv.deadlineMs !== null && (
                  <span className={styles.stepSub}>
                    {scaduto ? 'scaduta il ' : 'scade il '}
                    {fmtData(sv.deadlineMs)}
                  </span>
                )}
              </>
            );
          } else {
            meta = <span className={styles.stepSub}>scadenza non disponibile</span>;
          }

          return (
            <div
              key={sv.step}
              className={`${styles.stepperStep} ${segLeft} ${segRight}`}
              data-testid={`progress-step-${sv.step}`}
              data-state={sv.node}
            >
              <div className={`${styles.stepNode} ${classeNodo(sv.node)}`}>{glyph}</div>
              <div className={styles.stepBody}>
                <span className={styles.stepName}>{STEP_NAME[sv.step]}</span>
                <span className={styles.stepWindow}>{stepWindowLabel(report, sv.step)}</span>
                <span className={styles.stepMeta}>{meta}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
